import { Suspense, type ReactElement, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { LoadingState } from '../feedback/index.js';
import { MobileTabBar, Sidebar, Topbar, type NavItem, type SidebarGroup } from './chrome.js';
import { SeatBadge, type Seat } from '../system/index.js';

export interface AppShellProps {
  /**
   * Whose half of the product this is — audit finding C3.
   *
   * Customer, owner and support chrome were pixel-identical, and the only thing
   * telling an owner they were in the owner half was the words in a sidebar.
   * The seat supplies three signals at once and no new colour: a word in the
   * topbar, and — for the two WORKSPACE seats — the sunken page ground and the
   * denser rhythm §7 asks for. A storefront and a workspace should not have the
   * same floor.
   */
  seat?: Seat;
  /**
   * Replaces the default `Topbar` outright — the public pages pass
   * `PublicHeader`. Supplying this makes `topNav` and `actions` the header's
   * business rather than the shell's.
   */
  header?: ReactNode;
  /** Rendered in the topbar on md+. */
  topNav?: NavItem[];
  /** Persistent from lg. */
  sidebar?: { label: string; groups: SidebarGroup[] };
  /** Bottom tabs on sm only. */
  tabs?: NavItem[];
  /** Optional 360px detail rail from xl. */
  rail?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}

/**
 * The one shell. PublicShell, CustomerShell, OwnerShell and SupportShell are
 * configurations of it rather than four copies — a second implementation is
 * how navigation drifts apart between roles.
 */
export function AppShell({
  seat,
  header,
  topNav,
  sidebar,
  tabs,
  rail,
  actions,
  children,
}: AppShellProps): ReactElement {
  const { pathname } = useLocation();

  /*
    The 640–1023 band had NO navigation at all.

    The tab bar hides at 640, the sidebar does not appear until 1024, and the
    authenticated shells never passed `topNav` — so every tablet, and every
    desktop browser window narrower than 1024, rendered the whole product with
    a brand mark, a theme toggle and Sign out. An owner on an iPad could not
    reach Fleet, Offers or Overview; a support user could not reach the booking
    lookup. All three seats, one dead band.

    The navigation was already declared; nothing needed inventing. The sidebar's
    groups ARE the canonical set, so they fill the topbar for exactly that band
    and step aside at 1024 where the sidebar takes over — which is why the CSS
    hides `.topbar__nav` again from `lg` when a sidebar exists.
  */
  const bandNav = topNav ?? (sidebar ? sidebar.groups.flatMap((group) => group.items) : undefined);

  return (
    <div
      className={`shell${seat === undefined ? '' : ` shell--${seat}`}${
        sidebar ? ' shell--has-sidebar' : ''
      }`}
    >
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      {header ?? (
        <Topbar
          {...(bandNav ? { nav: bandNav } : {})}
          {...(actions ? { actions } : {})}
          {...(seat === undefined ? {} : { seat })}
        />
      )}
      {/*
        Support may READ everything and change almost nothing. Saying so once,
        persistently, is kinder than nine disabled buttons — and it is the
        boundary an investigator needs to see before they start, not after they
        try something.
      */}
      {seat === 'support' ? (
        <p className="readonly-band">
          <SeatBadge seat="support" />
          Read-only oversight. You can see this record; you cannot change it.
        </p>
      ) : null}
      <div className="shell__main">
        {sidebar ? <Sidebar label={sidebar.label} groups={sidebar.groups} /> : null}
        <main id="main" className={`content${tabs ? ' content--tabbed' : ''}`}>
          {/*
            `key` on the location is what makes the entrance a ROUTE entrance
            rather than a one-off on first paint: without it React reuses the
            same node across navigations and the animation never re-runs, so
            every page after the first would arrive without the transition the
            first one had.
          */}
          <div key={pathname} className="content__inner enter">
            {/* Guarded routes are lazy chunks; the shell stays painted while
                one loads instead of the whole page blanking. */}
            <Suspense fallback={<LoadingState rows={4} label="Loading" />}>
              {children ?? <Outlet />}
            </Suspense>
          </div>
        </main>
        {rail ? <aside className="rail">{rail}</aside> : null}
      </div>
      {tabs ? <MobileTabBar items={tabs} /> : null}
    </div>
  );
}
